// AuthProvider: restaura a sessão pelo backend (OIDC ou fake), lê `/me` pelo cliente da API e expõe o estado.
import { type MeResponse, getMe } from '@giyus/api-client';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApiClient } from '../api/apiClientContext';
import { AuthContext, type AuthContextValue } from './authContext';
import { isSuperAdminUser } from './greenfield';
import { type AuthBackend, type AuthSnapshot, isAuthCallbackPath } from './types';

const SUPER_ADMIN_ALL_NETWORK_ROLES = [
  'SUPER_USER',
  'HOLDING_ADMIN',
  'HOLDING_LEADERSHIP',
  'HOLDING_OPERATIONS_PLANNER',
  'STARTUP_SPECIALIST',
  'QUALITY_LEAD',
  'INFOSEC_LEAD',
  'DPO',
  'PRODUCT_OWNER',
  'PEOPLE_ADMIN',
  'LEGAL',
  'SUPPLIER_MANAGER',
] as const;

function enrichSuperAdminMe(me: MeResponse): MeResponse {
  if (!isSuperAdminUser(me)) {
    return me;
  }
  const mergedNetworkRoles = Array.from(
    new Set([...(me.networkAccess?.roles ?? []), ...SUPER_ADMIN_ALL_NETWORK_ROLES]),
  );
  const enrichedContexts = (me.contexts ?? []).map((ctx) => {
    const existingRoleCodes = new Set(ctx.effectiveRoles.map((r) => r.role));
    const extraRoles = [
      ...(!existingRoleCodes.has('SUPER_USER')
        ? [{ role: 'SUPER_USER', scopeType: 'Tenant' as const, scopeId: ctx.tenantId }]
        : []),
      ...(!existingRoleCodes.has('TENANT_ADMIN')
        ? [{ role: 'TENANT_ADMIN', scopeType: 'Tenant' as const, scopeId: ctx.tenantId }]
        : []),
    ];
    return extraRoles.length > 0
      ? { ...ctx, effectiveRoles: [...ctx.effectiveRoles, ...extraRoles] }
      : ctx;
  });
  return {
    ...me,
    networkAccess: {
      hasAccess: true,
      roles: mergedNetworkRoles,
    },
    contexts: enrichedContexts,
  };
}

export interface AuthProviderProps {
  readonly backend: AuthBackend;
  /** Rota interna atual, usada como `returnTo` no redirect (padrão: `location.pathname + search`). */
  readonly currentPath?: () => string;
  readonly children: ReactNode;
}

function defaultCurrentPath(): string {
  if (typeof window === 'undefined') {
    return '/';
  }
  return `${window.location.pathname}${window.location.search}`;
}

export function AuthProvider({
  backend,
  currentPath = defaultCurrentPath,
  children,
}: AuthProviderProps) {
  const client = useApiClient();
  const [snapshot, setSnapshot] = useState<AuthSnapshot>({
    status: 'initializing',
    me: null,
    error: null,
  });
  const initialized = useRef(false);

  const loadMe = useCallback(async (): Promise<MeResponse> => {
    const rawMe = await getMe(client);
    const me = enrichSuperAdminMe(rawMe);
    setSnapshot({ status: 'authenticated', me, error: null });
    return me;
  }, [client]);

  const signIn = useCallback(async (): Promise<void> => {
    setSnapshot({ status: 'signing-in', me: null, error: null });
    await backend.signIn(currentPath());
    if (backend.mode === 'fake') {
      await loadMe();
    }
  }, [backend, currentPath, loadMe]);

  const signOut = useCallback(async (): Promise<void> => {
    await backend.signOut();
    setSnapshot({ status: 'signing-in', me: null, error: null });
  }, [backend]);

  const refreshMe = useCallback(async (): Promise<void> => {
    try {
      await loadMe();
    } catch {
      await backend.signOut();
      await signIn();
    }
  }, [backend, loadMe, signIn]);

  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    if (isAuthCallbackPath(currentPath())) {
      // Sem isto o callback viraria um laço: initialize() ainda não vê sessão → signIn() → novo authorize → novo code…
      return;
    }
    void (async () => {
      try {
        const hasSession = await backend.initialize();
        if (hasSession) {
          try {
            await loadMe();
          } catch {
            await backend.signOut();
            await signIn();
          }
        } else {
          await signIn();
        }
      } catch (error: unknown) {
        setSnapshot({ status: 'error', me: null, error });
      }
    })();
  }, [backend, currentPath, loadMe, signIn]);

  // Sessão perdida por trás (renovação silenciosa falhou, token expirou, REQ-AUTH-012/013): volta ao login interativo.
  useEffect(() => {
    if (!backend.onSessionLost) {
      return undefined;
    }
    return backend.onSessionLost(() => {
      void signIn();
    });
  }, [backend, signIn]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...snapshot, backend, signIn, signOut, refreshMe }),
    [snapshot, backend, signIn, signOut, refreshMe],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
